import {
  Component,
  ElementRef,
  HostListener,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { LucideAngularModule } from 'lucide-angular';
import { I18nService } from '../../../../core';
import {
  MAX_BOARD_STAGE_METRICS,
  NUMERIC_AGGREGATION,
  type BoardStageMetricConfig,
  type BoardStageMetricEntry,
  type NumericAggregationCode,
  normalizeBoardStageMetricConfig,
} from '../../models/board-stage-metric.model';

export interface BoardStageMetricFieldOption {
  fieldId: string;
  name: string;
}

@Component({
  selector: 'app-board-stage-metric-picker',
  standalone: true,
  imports: [LucideAngularModule],
  template: `
    <div class="picker-root" #root>
      <button
        type="button"
        class="stage-metrics-trigger"
        [class.stage-metrics-trigger--tinted]="onTintedHeader()"
        [attr.aria-expanded]="menuOpen()"
        [attr.aria-haspopup]="true"
        [attr.aria-label]="triggerAriaResolved()"
        [attr.title]="metricsTriggerTooltip()"
        (click)="onTriggerClick($event)"
      >
        <span class="trigger-icon-wrap" aria-hidden="true">
          <lucide-icon name="bar-chart-3" class="trigger-icon"></lucide-icon>
        </span>
      </button>

      @if (menuOpen()) {
        <div class="stage-metrics-panel" role="dialog" [attr.aria-label]="panelAria()">
          <p class="panel-hint">{{ hintText() }}</p>

          @if (config().metrics.length === 0) {
            <p class="panel-empty">{{ noneLabel() }}</p>
          } @else {
            <ul class="metric-list" [attr.aria-label]="activeListAria()">
              @for (entry of config().metrics; track i; let i = $index) {
                <li class="metric-list-item">
                  <span class="metric-list-label">{{ formatEntry(entry) }}</span>
                  <button
                    type="button"
                    class="metric-remove-btn"
                    [attr.aria-label]="removeAria()"
                    (click)="removeAt(i, $event)"
                  >
                    <lucide-icon name="x" class="metric-remove-icon"></lucide-icon>
                  </button>
                </li>
              }
            </ul>
          }

          @if (config().metrics.length < maxMetrics()) {
            <div class="add-block">
              <label class="add-label">{{ fieldLabel() }}</label>
              <select
                class="add-select"
                [value]="draftFieldId()"
                (change)="onFieldSelect($any($event.target).value)"
              >
                <option value="">{{ fieldPlaceholder() }}</option>
                @for (opt of sumFieldOptions(); track opt.fieldId) {
                  <option [value]="opt.fieldId">{{ opt.name }}</option>
                }
              </select>

              <label class="add-label">{{ metricLabel() }}</label>
              <select
                class="add-select"
                [disabled]="!draftFieldId()"
                [value]="draftAggregationStr()"
                (change)="onMetricKindSelect($any($event.target).value)"
              >
                @for (m of draftMetricOptions(); track m.key) {
                  <option [value]="m.value">{{ m.label }}</option>
                }
              </select>

              <button
                type="button"
                class="add-btn"
                [disabled]="!canAddDraft()"
                (click)="addDraft($event)"
              >
                {{ addButtonLabel() }}
              </button>
            </div>
          }
        </div>
      }
    </div>
  `,
  styles: [
    `
      :host {
        display: inline-flex;
        position: relative;
        flex-shrink: 0;
      }

      .picker-root {
        position: relative;
      }

      .stage-metrics-trigger {
        box-sizing: border-box;
        width: 32px;
        height: 32px;
        padding: 0;
        border-radius: 10px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        line-height: 0;
        cursor: pointer;
        flex-shrink: 0;
        transition:
          background-color 140ms ease,
          border-color 140ms ease,
          color 140ms ease;
      }

      .stage-metrics-trigger--tinted {
        background: rgba(15, 23, 42, 0.32);
        border: 1px solid rgba(255, 255, 255, 0.42);
        color: #ffffff;
      }

      .stage-metrics-trigger--tinted:hover {
        background: rgba(15, 23, 42, 0.48);
        border-color: rgba(255, 255, 255, 0.55);
      }

      .stage-metrics-trigger:not(.stage-metrics-trigger--tinted) {
        background: #ffffff;
        border: 1px solid #dfd6c8;
        color: #4b5563;
        box-shadow: 0 1px 1px rgb(15 23 42 / 0.03);
      }

      .stage-metrics-trigger:not(.stage-metrics-trigger--tinted):hover {
        background: #fbf8f3;
        border-color: #ccbda8;
        color: #111827;
      }

      .trigger-icon-wrap {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 18px;
        height: 18px;
        flex-shrink: 0;
        line-height: 0;
        pointer-events: none;
      }

      :host ::ng-deep .trigger-icon-wrap svg {
        display: block;
        width: 16px;
        height: 16px;
        flex-shrink: 0;
      }

      .stage-metrics-panel {
        position: absolute;
        top: calc(100% + 6px);
        right: 0;
        min-width: 260px;
        max-width: min(360px, 94vw);
        border: 1px solid var(--border);
        border-radius: 12px;
        background: var(--surface-elevated);
        box-shadow: 0 14px 40px -18px rgba(15, 23, 42, 0.45);
        padding: 10px;
        z-index: 45;
        display: grid;
        gap: 10px;
      }

      .panel-hint {
        margin: 0;
        font-size: 11px;
        font-weight: 600;
        color: var(--muted, #6b7280);
        line-height: 1.35;
      }

      .panel-empty {
        margin: 0;
        font-size: 12px;
        color: var(--muted, #6b7280);
      }

      .metric-list {
        list-style: none;
        margin: 0;
        padding: 0;
        display: grid;
        gap: 4px;
      }

      .metric-list-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        padding: 6px 8px;
        border-radius: 8px;
        background: color-mix(in srgb, var(--hover) 55%, transparent);
      }

      .metric-list-label {
        font-size: 12px;
        font-weight: 600;
        color: var(--text);
        line-height: 1.3;
        min-width: 0;
      }

      .metric-remove-btn {
        flex-shrink: 0;
        width: 28px;
        height: 28px;
        border: 0;
        border-radius: 8px;
        background: transparent;
        color: var(--muted, #6b7280);
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }

      .metric-remove-btn:hover {
        background: color-mix(in srgb, var(--danger, #dc2626) 12%, transparent);
        color: var(--danger, #dc2626);
      }

      .metric-remove-icon {
        width: 14px;
        height: 14px;
      }

      .add-block {
        display: grid;
        gap: 6px;
        padding-top: 4px;
        border-top: 1px solid var(--border);
      }

      .add-label {
        font-size: 10px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: var(--muted, #6b7280);
      }

      .add-select {
        width: 100%;
        height: 36px;
        border-radius: 10px;
        border: 1px solid var(--border);
        background: var(--bg-surface);
        color: var(--text);
        font-size: 13px;
        padding: 0 10px;
      }

      .add-select:disabled {
        opacity: 0.55;
        cursor: not-allowed;
      }

      .add-btn {
        margin-top: 4px;
        height: 34px;
        border-radius: 10px;
        border: 1px solid var(--border);
        background: var(--bg-surface);
        color: var(--text);
        font-size: 12px;
        font-weight: 700;
        cursor: pointer;
      }

      .add-btn:hover:not(:disabled) {
        background: color-mix(in srgb, var(--hover) 75%, transparent);
      }

      .add-btn:disabled {
        opacity: 0.45;
        cursor: not-allowed;
      }
    `,
  ],
})
export class BoardStageMetricPickerComponent {
  private readonly i18n = inject(I18nService);
  readonly rootRef = viewChild<ElementRef<HTMLElement>>('root');

  readonly sumFieldOptions = input<BoardStageMetricFieldOption[]>([]);
  readonly config = input.required<BoardStageMetricConfig>();
  readonly maxMetrics = input<number>(MAX_BOARD_STAGE_METRICS);
  readonly onTintedHeader = input(false);
  readonly panelAria = input<string>('');
  readonly triggerAriaLabel = input<string>('');

  readonly configChange = output<BoardStageMetricConfig>();

  readonly menuOpen = signal(false);
  readonly draftFieldId = signal('');
  readonly draftMetricKind = signal<NumericAggregationCode>(NUMERIC_AGGREGATION.Sum);

  readonly draftAggregationStr = computed(() => String(this.draftMetricKind()));

  readonly draftMetricOptions = computed(() => {
    const fid = this.draftFieldId();
    if (!fid) return [] as { key: string; value: string; label: string }[];
    return [
      { key: '1', value: '1', label: this.metricTypeSumLabel() },
      { key: '2', value: '2', label: this.metricTypeAvgLabel() },
      { key: '3', value: '3', label: this.metricTypeMinLabel() },
      { key: '4', value: '4', label: this.metricTypeMaxLabel() },
      { key: '5', value: '5', label: this.metricTypeProductLabel() },
      { key: '6', value: '6', label: this.metricTypeSubtractLabel() },
      { key: '7', value: '7', label: this.metricTypeDivideLabel() },
    ];
  });

  readonly hintText = computed(() =>
    this.i18n.t(
      'workflowAggregations.stageMetricsMaxHint',
      undefined,
      `Até ${this.maxMetrics()} métricas por etapa.`
    )
  );

  readonly noneLabel = computed(() =>
    this.i18n.t('workflowAggregations.stageMetricNone', undefined, 'Nenhuma métrica')
  );

  readonly fieldLabel = computed(() =>
    this.i18n.t('workflowAggregations.stageMetricFieldLabel', undefined, 'Campo')
  );

  readonly fieldPlaceholder = computed(() =>
    this.i18n.t('workflowAggregations.stageMetricFieldPlaceholder', undefined, 'Escolha um campo')
  );

  readonly metricLabel = computed(() =>
    this.i18n.t('workflowAggregations.stageMetricTypeLabel', undefined, 'Métrica')
  );

  readonly addButtonLabel = computed(() =>
    this.i18n.t('workflowAggregations.stageMetricAdd', undefined, 'Adicionar')
  );

  readonly removeAria = computed(() =>
    this.i18n.t('workflowAggregations.stageMetricRemoveAria', undefined, 'Remover métrica')
  );

  readonly activeListAria = computed(() =>
    this.i18n.t('workflowAggregations.stageMetricActiveListAria', undefined, 'Métricas ativas')
  );

  /** Tooltip curto (hover): métricas da etapa — não confundir com aria-label mais descritivo. */
  readonly metricsTriggerTooltip = computed(() =>
    this.i18n.t('workflowAggregations.stageMetricsTriggerTitle', undefined, 'Métricas da etapa')
  );

  readonly triggerAriaResolved = computed(() => {
    const custom = this.triggerAriaLabel().trim();
    return custom || this.metricsTriggerTooltip();
  });

  metricTypeSumLabel(): string {
    return this.i18n.t('workflowAggregations.metricTypeSum', undefined, 'Soma');
  }

  metricTypeAvgLabel(): string {
    return this.i18n.t('workflowAggregations.metricTypeAvg', undefined, 'Média');
  }

  metricTypeMinLabel(): string {
    return this.i18n.t('workflowAggregations.metricTypeMin', undefined, 'Mínimo');
  }

  metricTypeMaxLabel(): string {
    return this.i18n.t('workflowAggregations.metricTypeMax', undefined, 'Máximo');
  }

  metricTypeProductLabel(): string {
    return this.i18n.t('workflowAggregations.metricTypeProduct', undefined, 'Multiplicar');
  }

  metricTypeSubtractLabel(): string {
    return this.i18n.t('workflowAggregations.metricTypeSubtract', undefined, 'Subtração (mais antigo − resto)');
  }

  metricTypeDivideLabel(): string {
    return this.i18n.t('workflowAggregations.metricTypeDivide', undefined, 'Divisão (mais antigo ÷ produto resto)');
  }

  aggregationLabel(code: NumericAggregationCode): string {
    switch (code) {
      case NUMERIC_AGGREGATION.Sum:
        return this.metricTypeSumLabel();
      case NUMERIC_AGGREGATION.Average:
        return this.metricTypeAvgLabel();
      case NUMERIC_AGGREGATION.Min:
        return this.metricTypeMinLabel();
      case NUMERIC_AGGREGATION.Max:
        return this.metricTypeMaxLabel();
      case NUMERIC_AGGREGATION.Product:
        return this.metricTypeProductLabel();
      case NUMERIC_AGGREGATION.SubtractOldestMinusRest:
        return this.metricTypeSubtractLabel();
      case NUMERIC_AGGREGATION.DivideOldestByProductRest:
        return this.metricTypeDivideLabel();
      default:
        return this.metricTypeSumLabel();
    }
  }

  formatEntry(entry: BoardStageMetricEntry): string {
    const name =
      this.sumFieldOptions().find((o) => o.fieldId === entry.fieldId)?.name ?? entry.fieldId;
    if (entry.kind === 'sum') {
      const prefix = this.i18n.t('workflowAggregations.metricSumPrefix', undefined, 'Soma — ');
      return `${prefix}${name}`;
    }
    const aggLabel = this.aggregationLabel(entry.aggregation);
    return `${aggLabel} — ${name}`;
  }

  onTriggerClick(event: MouseEvent): void {
    event.stopPropagation();
    const next = !this.menuOpen();
    this.menuOpen.set(next);
    if (next) {
      this.draftFieldId.set('');
      this.draftMetricKind.set(NUMERIC_AGGREGATION.Sum);
    }
  }

  onFieldSelect(value: string): void {
    this.draftFieldId.set(value);
    if (value) {
      this.draftMetricKind.set(NUMERIC_AGGREGATION.Sum);
    }
  }

  onMetricKindSelect(value: string): void {
    const n = Number.parseInt(value, 10);
    if (n >= 1 && n <= 7) {
      this.draftMetricKind.set(n as NumericAggregationCode);
    }
  }

  private metricCompositeKey(m: BoardStageMetricEntry): string | null {
    if (m.kind === 'sum') return `${m.fieldId}:${NUMERIC_AGGREGATION.Sum}`;
    if (m.kind === 'numeric') return `${m.fieldId}:${m.aggregation}`;
    return null;
  }

  canAddDraft(): boolean {
    const fid = this.draftFieldId();
    if (!fid) return false;
    if (this.config().metrics.length >= this.maxMetrics()) return false;
    const kind = this.draftMetricKind();
    const metrics = this.config().metrics;
    const agg = kind;
    const draftKey = `${fid}:${agg}`;
    return !metrics.some((m) => this.metricCompositeKey(m) === draftKey);
  }

  addDraft(event: MouseEvent): void {
    event.stopPropagation();
    if (!this.canAddDraft()) return;
    const fid = this.draftFieldId();
    const kind = this.draftMetricKind();
    const entry: BoardStageMetricEntry = { kind: 'numeric', fieldId: fid, aggregation: kind };
    const next = normalizeBoardStageMetricConfig({
      metrics: [...this.config().metrics, entry],
    });
    this.configChange.emit(next);
    this.draftFieldId.set('');
    this.draftMetricKind.set(NUMERIC_AGGREGATION.Sum);
  }

  removeAt(index: number, event: MouseEvent): void {
    event.stopPropagation();
    const m = [...this.config().metrics];
    m.splice(index, 1);
    this.configChange.emit(normalizeBoardStageMetricConfig({ metrics: m }));
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.menuOpen()) return;
    const root = this.rootRef()?.nativeElement;
    if (root && !root.contains(event.target as Node)) {
      this.menuOpen.set(false);
    }
  }
}
